import { useState } from "react";
import { Ruler, CheckCircle2, XCircle, AlertTriangle, Save, X } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "../lib/api";
import { useRooms } from "../lib/rooms";
import FitDiagram from "./FitDiagram";

const TIGHT = 4;

export function checkFit(p, s) {
  const normal = p.w <= s.w && p.d <= s.d;
  const turned = p.d <= s.w && p.w <= s.d;
  const turn = !normal && turned;
  const pw = turn ? p.d : p.w, pd = turn ? p.w : p.d;
  const spare = { w: s.w - pw, h: s.h - p.h, d: s.d - pd };
  const fits = p.h <= s.h && (normal || turned);
  return { fits, turn, pw, pd, spare, tight: fits && Math.min(spare.w, spare.h, spare.d) < TIGHT };
}

function Result({ p, s }) {
  const r = checkFit(p, s);
  const tone = !r.fits ? "text-red-400" : r.tight ? "text-amber-400" : "text-emerald-400";
  const Icon = !r.fits ? XCircle : r.tight ? AlertTriangle : CheckCircle2;
  const msg = !r.fits ? "Doesn't fit this space" : r.tight ? "Fits, but it's a tight squeeze" : "Fits comfortably";
  return (
    <div data-testid="room-fit-result" className="space-y-3">
      <p data-testid="room-fit-verdict" className={`flex items-center gap-2 font-display font-bold text-lg ${tone}`}><Icon size={20} />{msg}</p>
      {r.turn && r.fits && <p className="text-xs text-slate-400" data-testid="room-fit-turned">Fits when turned sideways (width and depth swapped).</p>}
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        {[["Width", r.spare.w], ["Height", r.spare.h], ["Depth", r.spare.d]].map(([k, v]) => (
          <div key={k} data-testid={`room-fit-spare-${k.toLowerCase()}`} className={`rounded-xl p-2 bg-white/5 ${v < 0 ? "text-red-400" : v < TIGHT ? "text-amber-400" : "text-emerald-400"}`}>
            <p className="text-slate-500">{k}</p><p className="font-bold">{v < 0 ? `${Math.abs(v)} cm short` : `${v} cm spare`}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-4">
        <FitDiagram testid="room-fit-front-diagram" label="Front view (W × H)" sw={s.w} sh={s.h} pw={r.pw} ph={p.h} ok={r.spare.w >= 0 && r.spare.h >= 0} />
        <FitDiagram testid="room-fit-top-diagram" label="Top view (W × D)" sw={s.w} sh={s.d} pw={r.pw} ph={r.pd} ok={r.spare.w >= 0 && r.spare.d >= 0} />
      </div>
      <p className="text-[11px] text-slate-500"><span className="text-[#00f0ff]">- - -</span> your space &nbsp; ▮ product</p>
    </div>
  );
}

export default function RoomFit({ dimensions }) {
  const p = { h: +dimensions.h, w: +dimensions.w, d: +dimensions.d };
  const { rooms, save, remove } = useRooms();
  const [s, setS] = useState({ w: "", h: "", d: "" });
  const [name, setName] = useState("");
  const ready = s.w > 0 && s.h > 0 && s.d > 0;
  const space = { w: +s.w, h: +s.h, d: +s.d };
  const doSave = async () => {
    try { await save({ name: name.trim() || "My space", ...space }); setName(""); toast.success("Space saved for every product"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const inp = "w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm focus:border-[#ff5500] outline-none";
  return (
    <section data-testid="room-fit-section" className="mt-10 rounded-3xl bg-[#12141c] border border-white/10 p-6 grid md:grid-cols-2 gap-8">
      <div className="space-y-4">
        <h2 className="text-lg font-display font-bold flex items-center gap-2"><Ruler size={18} className="text-[#00f0ff]" />Will it fit?</h2>
        <p className="text-sm text-slate-400">Product size: <b className="text-white">W {p.w} × H {p.h} × D {p.d} cm</b>. Measure your space and enter it below.</p>
        {rooms.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="saved-rooms-list">
            {rooms.map((r) => (
              <span key={r.id} className="flex items-center gap-1 rounded-full bg-white/5 border border-white/10 text-xs pl-3 pr-1 py-1">
                <button data-testid={`saved-room-${r.id}`} onClick={() => setS({ w: r.w, h: r.h, d: r.d })} className="hover:text-[#ff5500]">{r.name} · {r.w}×{r.h}×{r.d}</button>
                <button data-testid={`delete-room-${r.id}`} onClick={() => remove(r.id)} aria-label="remove" className="p-1 rounded-full hover:bg-white/10"><X size={12} /></button>
              </span>
            ))}
          </div>
        )}
        <div className="grid grid-cols-3 gap-2">
          {[["w", "Width"], ["h", "Height"], ["d", "Depth"]].map(([k, l]) => (
            <label key={k} className="text-xs text-slate-400 space-y-1">{l} (cm)
              <input data-testid={`room-fit-${k}-input`} type="number" min="1" value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} className={inp} />
            </label>
          ))}
        </div>
        {ready && (
          <div className="flex gap-2">
            <input data-testid="room-name-input" placeholder="Name it, e.g. Kitchen nook" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} className={inp} />
            <button data-testid="save-room-btn" onClick={doSave} className="shrink-0 flex items-center gap-1 px-4 rounded-full bg-white text-black text-sm font-semibold hover:bg-[#ff5500] hover:text-white transition-colors"><Save size={14} />Save</button>
          </div>
        )}
      </div>
      {ready ? <Result p={p} s={space} /> : <p data-testid="room-fit-empty" className="self-center text-sm text-slate-500">Enter width, height and depth of your space to see if it fits.</p>}
    </section>
  );
}
